import { useCallback, useState } from 'react'
import { useSnackbar } from 'notistack'
import { t } from '@lingui/core/macro'
import { useUpdatePatientLocationMutation } from '@/core/graphql/hooks'
import { LocationOption, PatientLocationData } from '../lib/patient-location.types'

type SelectedPlace = { description: string; placeId: string }

export function usePatientLocation(consultationId: string, initialLocation: PatientLocationData | null, onSaved: () => void) {
  const [selected, setSelected] = useState<LocationOption | null>(null)
  const [selectedPlace, setSelectedPlace] = useState<SelectedPlace | null>(
    initialLocation?.description ? { description: initialLocation.description, placeId: initialLocation.placeId ?? '' } : null
  )
  const [updatePatientLocation, { loading: isSaving }] = useUpdatePatientLocationMutation()
  const { enqueueSnackbar } = useSnackbar()

  const isOther = selected === 'OTHER'

  const getDescription = (): string | null => {
    if (!selected) return null
    if (isOther) return selectedPlace?.description ?? null
    return null
  }

  const handleSelect = useCallback((option: LocationOption) => {
    setSelected(option)
    if (option !== 'OTHER') setSelectedPlace(null)
  }, [])

  const handlePlaceSelect = useCallback((place: google.maps.places.AutocompletePrediction) => {
    setSelectedPlace({ description: place.description, placeId: place.place_id })
  }, [])

  const handleSave = useCallback(
    async (description: string) => {
      if (!description) return

      try {
        await updatePatientLocation({
          variables: {
            payload: {
              consultationId,
              description,
              placeId: isOther ? selectedPlace?.placeId : (selected ?? undefined),
            },
          },
        })
        enqueueSnackbar(t`Location saved`, { variant: 'success' })
        onSaved()
      } catch (error) {
        console.error('Failed to save patient location:', error)
        enqueueSnackbar(t`Failed to save location`, { variant: 'error' })
      }
    },
    [consultationId, selected, selectedPlace, isOther, onSaved, updatePatientLocation, enqueueSnackbar]
  )

  return {
    selected,
    selectedPlace,
    isOther,
    isSaving,
    handleSelect,
    handlePlaceSelect,
    handleSave,
    getDescription,
  }
}
